import { NavLink, RouteObject, useLocation } from "react-router";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from "@/assets";
import { FeatureFlag, LOGICAL_CAAS_KEY } from "@/constants";
import AccessGuard from "@/components/access-guard";
import React, { lazy } from "react";
import { TabTitle } from "../faults/deprecated/faults-dashboard-page-v2/constants";
import { useIsCustomer } from "@/hooks/useIsCustomer";
import { MaintenanceSidebarGroup } from "@/components/maintenance-sidebar-group";
import { useFlags } from "launchdarkly-react-client-sdk";

const OfflineInsightPage = lazy(
    () => import("../faults/deprecated/offline/offline-insight-page"),
);
const DeprecatedDevicePage = lazy(() => import("../device-page/device-page"));
const WarningInsightPage = lazy(
    () => import("../faults/deprecated/warnings/warning-insight-page"),
);
const FaultDetailsPage = lazy(
    () => import("../faults/deprecated/faulted/faulted-insight-page"),
);

export const faultLinks: {
    tabId: TabTitle;
    display: string;
    children: RouteObject[];
    flag?: FeatureFlag;
    hideWhenFlag?: FeatureFlag;
}[] = [
    {
        tabId: TabTitle.Faulted,
        display: "Faulted devices",
        children: [
            {
                path: ":deviceId/fault-insight/:faultId",
                handle: {
                    breadcrumbKey: ":faultId",
                },
                element: <FaultDetailsPage />,
            },
        ],
    },
    {
        tabId: TabTitle.Offline,
        display: "Offline devices",
        children: [
            {
                path: ":deviceId/offline-insight",
                handle: {
                    breadcrumbKey: ":deviceId",
                },
                element: <OfflineInsightPage />,
            },
        ],
    },
    {
        tabId: TabTitle.Unknown,
        display: "Unknown devices",
        children: [
            {
                path: ":deviceId",
                handle: {
                    breadcrumbKey: ":deviceId",
                },
                element: <DeprecatedDevicePage />,
            },
        ],
    },
    {
        tabId: TabTitle.NonOperational,
        display: "Non-operational devices",
        hideWhenFlag: FeatureFlag.IL_3981_OPERATIONAL_FILTER,
        children: [
            {
                path: ":deviceId",
                handle: {
                    breadcrumbKey: ":deviceId",
                },
                element: <DeprecatedDevicePage />,
            },
        ],
    },
    {
        tabId: TabTitle.Warnings,
        display: "Warnings",
        children: [
            {
                path: ":deviceId/:warningPageType/:warningId",
                handle: {
                    breadcrumbKey: ":warningId",
                },
                element: <WarningInsightPage />,
            },
        ],
    },
];

export const NetworkSidebar = ({
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const isCustomer = useIsCustomer();
    const location = useLocation();
    const flags = useFlags();
    return (
        <Sidebar {...props}>
            {props.children}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Dashboards</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/network/uptime`}
                                    >
                                        <span>Uptime</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <AccessGuard
                                featureFlags={[FeatureFlag.IL_3114_CSR]}
                                flagOffElement={null}
                                unauthorizedBehaviour="hide"
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/network/sessions`}
                                        >
                                            <span>Sessions</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                            {flags[
                                FeatureFlag.IL_4026_UPLIFT_FAULTS_DASHBOARD
                            ] ? (
                                <>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/faulted-devices`}
                                            >
                                                <span>Faulted devices</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/offline-devices`}
                                            >
                                                <span>Offline devices</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/unknown-devices`}
                                            >
                                                <span>Unknown devices</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/warnings`}
                                            >
                                                <span>Warnings</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </>
                            ) : (
                                <Collapsible
                                    className="group/collapsible"
                                    defaultOpen={location.pathname.includes(
                                        "network/faults/",
                                    )}
                                >
                                    <SidebarMenuItem>
                                        <CollapsibleTrigger className="relative w-full">
                                            <div className="absolute -left-6 flex h-10 items-center">
                                                <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90" />
                                            </div>
                                            <SidebarMenuButton asChild>
                                                <span>Faults</span>
                                            </SidebarMenuButton>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <SidebarMenuSub>
                                                {faultLinks
                                                    .filter(
                                                        ({ hideWhenFlag }) =>
                                                            !hideWhenFlag ||
                                                            !flags[
                                                                hideWhenFlag
                                                            ],
                                                    )
                                                    .map(
                                                        ({
                                                            display,
                                                            tabId,
                                                            flag,
                                                        }) => (
                                                            <AccessGuard
                                                                key={tabId}
                                                                featureFlags={
                                                                    flag
                                                                        ? [flag]
                                                                        : undefined
                                                                }
                                                                unauthorizedBehaviour="hide"
                                                            >
                                                                <SidebarMenuSubItem>
                                                                    <SidebarMenuButton
                                                                        asChild
                                                                    >
                                                                        <NavLink
                                                                            className={
                                                                                "text-default-color"
                                                                            }
                                                                            to={`/network/faults/${tabId.toLowerCase()}`}
                                                                        >
                                                                            <span>
                                                                                {
                                                                                    display
                                                                                }
                                                                            </span>
                                                                        </NavLink>
                                                                    </SidebarMenuButton>
                                                                </SidebarMenuSubItem>
                                                            </AccessGuard>
                                                        ),
                                                    )}
                                            </SidebarMenuSub>
                                        </CollapsibleContent>
                                    </SidebarMenuItem>
                                </Collapsible>
                            )}
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink
                                        className={"text-default-color"}
                                        to={`/network/sustainability`}
                                    >
                                        <span>Sustainability</span>
                                    </NavLink>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <AccessGuard
                                accessTags={["JETCharge"]}
                                unauthorizedBehaviour={"hide"}
                            >
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild>
                                        <NavLink
                                            className={"text-default-color"}
                                            to={`/network/caas/${LOGICAL_CAAS_KEY}`}
                                        >
                                            <span>CaaS</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </AccessGuard>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                {isCustomer && <MaintenanceSidebarGroup basePath="/network" />}

                <AccessGuard
                    permissions={["charge_transactions_read", "webhook_create"]}
                    permissionsSelector="any"
                    unauthorizedBehaviour={"hide"}
                >
                    <SidebarGroup>
                        <SidebarGroupLabel>Reports</SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <AccessGuard
                                    permissions={["charge_transactions_read"]}
                                    unauthorizedBehaviour={"hide"}
                                >
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/reports/create`}
                                                data-tour-step="create-report-tab"
                                            >
                                                <span>Create report</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </AccessGuard>
                                <AccessGuard
                                    permissions={["webhook_create"]}
                                    unauthorizedBehaviour={"hide"}
                                >
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <NavLink
                                                className={"text-default-color"}
                                                to={`/network/reports/scheduled`}
                                                data-tour-step="scheduled-reports-tab"
                                            >
                                                <span>Scheduled reports</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </AccessGuard>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </AccessGuard>
            </SidebarContent>
        </Sidebar>
    );
};
